import {
    Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from './KeyTakeaway';
import VizToggle, { VizType } from './VizToggle';
import { SpikeRow, ReportSection } from './types';

interface Props {
    data: ReportSection<SpikeRow>;
}

const SpikeAnalysisSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const barData = data.rows.map((r) => ({
        name: r.name.split(' ')[0],
        fullName: r.name,
        highest: r.highest_score,
        lowest: r.lowest_score,
        gap: r.spike_gap,
    }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-spike-analysis">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">3. Individual Strength and Growth Areas</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Team members with the largest gap between strongest and weakest domains.
                Large gaps suggest targeted coaching opportunities.
            </Typography>

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={Math.max(350, data.rows.length * 35)}>
                    <BarChart data={barData} layout="vertical" margin={{ left: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} />
                        <Tooltip
                            formatter={(val: number, name: string) => [val.toFixed(2), name]}
                            labelFormatter={(label: string, payload: readonly any[]) =>
                                payload?.[0]?.payload?.fullName || label
                            }
                        />
                        <Bar dataKey="highest" name="Strongest" fill={theme.palette.success.main} radius={[0, 4, 4, 0]} />
                        <Bar dataKey="lowest" name="Weakest" fill={theme.palette.error.main} radius={[0, 4, 4, 0]} />
                        <Legend />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Strongest</TableCell>
                                <TableCell align="right">Score</TableCell>
                                <TableCell>Weakest</TableCell>
                                <TableCell align="right">Score</TableCell>
                                <TableCell align="right">Gap</TableCell>
                                <TableCell align="right">Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.email}>
                                    <TableCell>{r.spikiest_rank}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>
                                        <Chip label={r.strongest_domain} size="small" color="success" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">{r.highest_score}</TableCell>
                                    <TableCell>
                                        <Chip label={r.weakest_domain} size="small" color="error" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right">{r.lowest_score}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{r.spike_gap}</TableCell>
                                    <TableCell align="right">{r.total_score}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default SpikeAnalysisSection;
